import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo, useState, type ReactNode } from 'react';
import type { Account, JournalEntry, JournalPending, JournalSource } from '@fsp/core';
import { api, type Me } from '../api';
import { cents, money, moneyInput } from '../form';
import { errorText, useI18n } from '../i18n';
import { Alert, PageHead, PanelShell } from '../layout';

interface Journal { entries: JournalEntry[]; pending: JournalPending[]; turnover: { account: string; debit: number; credit: number }[]; settings: { startDate: string | null; lockedUntil: string | null } }
interface Draft { id: number | null; day: string; description: string; lines: { account: string; debit: string; credit: string; note: string }[] }

const SOURCES: Record<JournalSource, string> = { invoice: 'Sąskaitos', bank: 'Bankas', cash: 'Kasa', z_report: 'Z ataskaitos', online: 'Mokėjimai internetu', payroll: 'Darbo užmokestis', depreciation: 'Nusidėvėjimas', asset: 'Ilgalaikis turtas', fx: 'Valiutos perkainojimas', vat: 'PVM sudengimas', dividend: 'Dividendai', manual: 'Rankiniai įrašai' };
const REASONS: Record<string, string> = {
  unexplained: 'Banko operacija nepaaiškinta',
  needs_accountant: 'Reikia buhalterio sprendimo',
  foreign_currency: 'Kita valiuta',
  cash_no_document: 'Kasos operacija be dokumento',
  paid_by_hand: 'Pažymėta apmokėta rankiniu būdu — mokėjimo būdas nežinomas',
};
const lastMonth = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return d.toLocaleDateString('sv-SE').slice(0, 7); };
const monthEnd = (ym: string) => { const [y, m] = ym.split('-').map(Number) as [number, number]; return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10); };
const emptyLine = () => ({ account: '', debit: '', credit: '', note: '' });

/**
 * Žurnalas: the month's double entries worked out from the documents, what waits for the accountant, the
 * accounts' turnover and the accountant's own entries (corrections, opening balances ...).
 */
export function JournalPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [month, setMonth] = useState(lastMonth);
  const [source, setSource] = useState<JournalSource | ''>('');
  const [draft, setDraft] = useState<Draft | null>(null);
  const from = `${month}-01`, to = monthEnd(month);
  const data = useQuery({ queryKey: ['journal', month], queryFn: () => api.get<Journal>(`api/v1/journal?from=${from}&to=${to}`) });
  const plan = useQuery({ queryKey: ['accounts'], queryFn: () => api.get<{ accounts: Account[] }>('api/v1/accounts') });
  const readOnly = me.company.role === 'viewer' || Boolean(me.viewAsAdmin);
  const act = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: async () => { setDraft(null); await queryClient.invalidateQueries({ queryKey: ['journal'] }); },
  });
  const names = useMemo(() => new Map((plan.data?.accounts ?? []).map((a) => [a.code, a.name])), [plan.data]);
  const postable = useMemo(() => (plan.data?.accounts ?? []).filter((a) => a.postable && !a.hidden), [plan.data]);
  const j = data.data;
  const shown = (j?.entries ?? []).filter((e) => !source || e.source === source);
  const name = (code: string) => names.get(code) ?? '';
  const locked = (date: string) => Boolean(j?.settings.lockedUntil && date <= j.settings.lockedUntil);

  const sourceLink = (s: JournalSource, id: number, label: string, outgoing?: boolean): ReactNode =>
    s === 'invoice' ? <Link to={outgoing === false ? '/incoming/$id' : '/outgoing/$id'} params={{ id: String(id) }}>{label}</Link>
      : s === 'bank' ? <Link to="/bank">{label}</Link> : s === 'cash' || s === 'z_report' ? <Link to="/cash">{label}</Link> : s === 'payroll' ? <Link to="/payroll">{label}</Link> : s === 'depreciation' || s === 'asset' ? <Link to="/fixed-assets">{label}</Link> : s === 'vat' ? <Link to="/declarations">{label}</Link> : label;

  const draftDebit = draft?.lines.reduce((s, l) => s + cents(l.debit), 0) ?? 0;
  const draftCredit = draft?.lines.reduce((s, l) => s + cents(l.credit), 0) ?? 0;
  const saveDraft = () => {
    if (!draft) return;
    const body = { day: draft.day, description: draft.description, lines: draft.lines.filter((l) => l.account).map((l) => ({ account: l.account, debit: cents(l.debit), credit: cents(l.credit), note: l.note })) };
    act.mutate(() => (draft.id ? api.put(`api/v1/journal/entries/${draft.id}`, body) : api.post('api/v1/journal/entries', body)));
  };
  const accountSelect = (value: string, onChange: (v: string) => void, classes?: string, label?: string) => (
    <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label ?? t('Sąskaita')} disabled={readOnly}>
      {value && !postable.some((a) => a.code === value) ? <option value={value}>{value} {name(value)}</option> : null}
      {value ? null : <option value="">{t('Pasirinkite sąskaitą')}</option>}
      {postable.filter((a) => !classes || classes.includes(a.code[0]!)).map((a) => <option key={a.code} value={a.code}>{a.code} {a.name}</option>)}
    </select>
  );

  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Buhalterija')} title={t('Žurnalas')} text={t('Mėnesio buhalteriniai įrašai pagal sąskaitų planą: sudaryti iš dokumentų automatiškai ir buhalterio įrašai.')} />}>
      <div className="form-actions journal-period">
        <input type="month" aria-label={t('Mėnuo')} value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
        <a className="button button--ghost button--small" href={`api/v1/journal.csv?from=${from}&to=${to}`}>{t('Žurnalas (CSV)')}</a>
      </div>
      {data.isError ? <Alert kind="error">{errorText(t, data.error)}</Alert> : null}
      {act.isError ? <Alert kind="error">{errorText(t, act.error)}</Alert> : null}

      {j ? <>
        {j.settings.lockedUntil && from <= j.settings.lockedUntil ? <Alert kind="success">{t('Knygos uždarytos iki {date}: šio laikotarpio įrašų keisti negalima.', { date: j.settings.lockedUntil })} <Link to="/books">{t('Didžioji knyga')}</Link></Alert> : null}
        {j.settings.startDate && to < j.settings.startDate ? <Alert kind="success">{t('Apskaita pradedama {date}: ankstesni dokumentai žurnale neregistruojami.', { date: j.settings.startDate })}</Alert> : null}
        {j.pending.length ? (
          <section className="panel-card journal-pending">
            <div className="card-head"><div><p className="eyebrow">{t('Laukia')}</p><h2>{t('Neįtraukta į žurnalą ({n})', { n: j.pending.length })}</h2></div></div>
            <p className="muted">{t('Šių operacijų programa negali užregistruoti pati: paaiškinkite jas banke ar kasoje arba įveskite rankinį įrašą.')}</p>
            <table className="journal-list">
              <tbody>
                {j.pending.map((p) => (
                  <tr key={p.id}>
                    <td className="journal-date">{p.date}</td>
                    <td>{sourceLink(p.source, p.sourceId, p.label)}<small>{t(REASONS[p.reason.split(':')[0]!] ?? p.reason)}</small></td>
                    <td className="right">{money(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ) : null}

        <section className="panel-card">
          <div className="card-head">
            <div><p className="eyebrow">{month}</p><h2>{t('Įrašai')}</h2></div>
            <div className="form-actions">
              <select aria-label={t('Šaltinis')} value={source} onChange={(e) => setSource(e.target.value as JournalSource | '')}>
                <option value="">{t('Visi')}</option>
                {(Object.keys(SOURCES) as JournalSource[]).map((s) => <option key={s} value={s}>{t(SOURCES[s])}</option>)}
              </select>
              {readOnly ? null : <button type="button" className="button button--primary button--small" onClick={() => setDraft({ id: null, day: to, description: '', lines: [emptyLine(), emptyLine()] })}>+ {t('Rankinis įrašas')}</button>}
            </div>
          </div>

          {draft ? (
            <div className="journal-draft">
              <div className="form-grid">
                <label>{t('Data')}<input type="date" value={draft.day} onChange={(e) => setDraft({ ...draft, day: e.target.value })} /></label>
                <label>{t('Aprašymas')}<input value={draft.description} maxLength={300} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder={t('pvz., Įžanginiai likučiai')} /></label>
              </div>
              <table className="journal-lines journal-lines--edit">
                <thead><tr><th>{t('Sąskaita')}</th><th className="right">{t('Debetas')}</th><th className="right">{t('Kreditas')}</th><th>{t('Pastaba')}</th><th /></tr></thead>
                <tbody>
                  {draft.lines.map((l, i) => {
                    const set = (patch: Partial<typeof l>) => setDraft({ ...draft, lines: draft.lines.map((x, k) => (k === i ? { ...x, ...patch } : x)) });
                    return (
                      <tr key={i}>
                        <td>{accountSelect(l.account, (v) => set({ account: v }))}</td>
                        <td><input inputMode="decimal" aria-label={t('Debetas')} value={l.debit} onChange={(e) => set({ debit: e.target.value, ...(e.target.value ? { credit: '' } : {}) })} /></td>
                        <td><input inputMode="decimal" aria-label={t('Kreditas')} value={l.credit} onChange={(e) => set({ credit: e.target.value, ...(e.target.value ? { debit: '' } : {}) })} /></td>
                        <td><input value={l.note} maxLength={200} aria-label={t('Pastaba')} onChange={(e) => set({ note: e.target.value })} /></td>
                        <td>{draft.lines.length > 2 ? <button type="button" className="icon-button" aria-label={t('Pašalinti')} onClick={() => setDraft({ ...draft, lines: draft.lines.filter((_, k) => k !== i) })}>×</button> : null}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot><tr>
                  <td><button type="button" className="text-link" onClick={() => setDraft({ ...draft, lines: [...draft.lines, emptyLine()] })}>+ {t('Eilutė')}</button></td>
                  <td className="right">{money(draftDebit)}</td><td className="right">{money(draftCredit)}</td>
                  <td colSpan={2}>{draftDebit !== draftCredit ? <span className="account-problem">{t('Skirtumas {sum}', { sum: money(draftDebit - draftCredit) })}</span> : null}</td>
                </tr></tfoot>
              </table>
              <div className="form-actions">
                <button type="button" className="button button--primary" disabled={act.isPending || !draft.description.trim() || !draftDebit || draftDebit !== draftCredit} onClick={saveDraft}>{t('Išsaugoti')}</button>
                <button type="button" className="button button--ghost" onClick={() => setDraft(null)}>{t('Atšaukti')}</button>
              </div>
            </div>
          ) : null}

          {shown.length ? shown.map((e) => (
            <article key={e.id} className="journal-entry">
              <header>
                <span className="journal-date">{e.date}</span>
                <strong>{sourceLink(e.source, e.sourceId, e.label, e.account ? e.account.direction === 'outgoing' : undefined)}</strong>
                <span className="shop-tag">{t(SOURCES[e.source])}</span>
                {e.source === 'manual' && !readOnly && !locked(e.date) ? <span className="journal-actions">
                  <button type="button" className="text-link" onClick={() => setDraft({ id: e.sourceId, day: e.date, description: e.label, lines: e.lines.map((l) => ({ account: l.account, debit: l.debit ? moneyInput(l.debit) : '', credit: l.credit ? moneyInput(l.credit) : '', note: l.note ?? '' })) })}>{t('Keisti')}</button>
                  <button type="button" className="text-link" onClick={() => { if (window.confirm(t('Ištrinti šį įrašą?'))) act.mutate(() => api.delete(`api/v1/journal/entries/${e.sourceId}`)); }}>{t('Ištrinti')}</button>
                </span> : null}
              </header>
              <table className="journal-lines">
                <tbody>
                  {e.lines.map((l, i) => (
                    <tr key={i}>
                      <td className="account-code">{l.account}</td>
                      <td>{name(l.account)}{l.partner || l.note ? <small>{l.partner ?? l.note}</small> : null}</td>
                      <td className="right">{l.debit ? money(l.debit) : ''}</td>
                      <td className="right">{l.credit ? money(l.credit) : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {e.account && !readOnly && !locked(e.date) ? <InvoiceAccount entry={e} select={accountSelect} onSave={(account, remember) => act.mutate(() => api.put(`api/v1/journal/invoices/${e.account!.invoiceId}/account`, { account, remember }))} /> : null}
            </article>
          )) : <p className="empty">{t('Šį mėnesį įrašų nėra.')}</p>}
        </section>

        {j.turnover.length ? (
          <section className="panel-card">
            <div className="card-head"><div><p className="eyebrow">{month}</p><h2>{t('Sąskaitų apyvartos')}</h2></div></div>
            <table className="journal-lines">
              <thead><tr><th>{t('Sąskaita')}</th><th /><th className="right">{t('Debetas')}</th><th className="right">{t('Kreditas')}</th></tr></thead>
              <tbody>
                {j.turnover.map((r) => <tr key={r.account}><td className="account-code">{r.account}</td><td>{name(r.account)}</td><td className="right">{money(r.debit)}</td><td className="right">{money(r.credit)}</td></tr>)}
              </tbody>
              <tfoot><tr><td /><td>{t('Iš viso')}</td><td className="right">{money(j.turnover.reduce((s, r) => s + r.debit, 0))}</td><td className="right">{money(j.turnover.reduce((s, r) => s + r.credit, 0))}</td></tr></tfoot>
            </table>
          </section>
        ) : null}
      </> : null}
    </PanelShell>
  );
}

/** An invoice's income or expense account: this invoice, or the partner's from now on. */
function InvoiceAccount({ entry, select, onSave }: { entry: JournalEntry; select: (value: string, onChange: (v: string) => void, classes?: string, label?: string) => ReactNode; onSave: (account: string | null, remember: boolean) => void }) {
  const { t } = useI18n();
  const a = entry.account!;
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState(a.code);
  const [remember, setRemember] = useState(false);
  const label = a.direction === 'outgoing' ? t('Pajamų sąskaita') : t('Sąnaudų sąskaita');
  if (!open) return (
    <p className="journal-account muted">
      {label}: {a.code} · {a.from === 'invoice' ? t('pasirinkta šiai sąskaitai') : a.from === 'partner' ? t('partnerio įprasta') : t('numatytoji')}
      {' '}<button type="button" className="text-link" onClick={() => setOpen(true)}>{t('Keisti')}</button>
    </p>
  );
  return (
    <div className="journal-account account-inline">
      {select(code, setCode, a.direction === 'outgoing' ? '5' : '126', label)}
      {a.partner ? <label className="check"><input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />{t('Ir kitoms šio partnerio sąskaitoms')}</label> : null}
      <button type="button" className="button button--primary button--small" onClick={() => onSave(code, remember)}>{t('Išsaugoti')}</button>
      {a.from === 'invoice' ? <button type="button" className="text-link" onClick={() => onSave(null, remember)}>{t('Numatytoji')}</button> : null}
      <button type="button" className="text-link" onClick={() => setOpen(false)}>{t('Atšaukti')}</button>
    </div>
  );
}
